import { useCallback, useEffect, useState, useRef } from 'react';
import throttle from 'lodash/throttle';

type Params = {
  onFocus?: Function;
  sectionRef: {
    current: HTMLElement;
  };
  itemRef: {
    current: HTMLElement;
  };
  offsetTop?: number;
  offsetBottom?: number;
  scrollable?: boolean;
};

type ReturnType = { focused: Boolean };

const DEFAULT_OFFSET = 100;

export function useFocusOnSeen({
  onFocus,
  sectionRef,
  itemRef,
  offsetTop = DEFAULT_OFFSET,
  offsetBottom,
  scrollable = true,
}: Params): ReturnType {
  const [focused, setFocused] = useState(false);

  const prevViewed = useRef(null);

  const checkView = useCallback(() => {
    const sectionNode = sectionRef?.current;
    const itemNode = itemRef?.current;

    const scrollTop = sectionNode?.scrollTop;
    const offsetHeight = sectionNode?.offsetHeight;
    const itemOffsetTop = itemNode?.offsetTop;
    const itempositionY = itemOffsetTop - scrollTop;

    const isFocused = offsetBottom
      ? itempositionY > offsetTop && itempositionY < offsetHeight / 2 + offsetBottom
      : itempositionY < offsetTop;
    if (prevViewed.current !== isFocused) {
      prevViewed.current = focused;
      setFocused(isFocused);
    }
  }, [itemRef, sectionRef, focused]);

  const throttleCheckView = useCallback(throttle(checkView, 100), [checkView]);

  useEffect(() => {
    if (scrollable) {
      checkView();
    }
  }, []);

  useEffect(() => {
    const sectionNode = sectionRef?.current;
    if (scrollable && sectionNode) {
      sectionNode.addEventListener('scroll', throttleCheckView);
    }
    return () => {
      if (scrollable && sectionNode) {
        sectionNode.removeEventListener('scroll', throttleCheckView);
      }
    };
  }, [sectionRef, checkView]);

  useEffect(() => {
    if (!prevViewed.current && focused && onFocus) onFocus();
  }, [focused]);

  return { focused };
}
